/* eslint-disable react/prop-types */
/* eslint-disable max-len */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Row,
  Spin,
  Col,
  Modal,
  message,
} from 'antd';
import Table from 'components/Table';
import { CheckCircleOutlined, CloseCircleOutlined, RedoOutlined } from '@ant-design/icons';
import { getEstadosGlobales } from 'api/catalogos';
import { getDeducciones } from 'api/nominas/deducciones/deducciones';
import { checkToken } from 'api/utils';
import FormSubmitControls from 'components/FormSubmitControls';
import Select from 'components/Select';
import ModalDelete from 'components/ModalDelete';
import ModalConfirmation from 'components/ModalConfirmation';
import { onError, onSuccess } from 'utils/handlers';
import { formatReceivedValues } from 'utils/formatValues';
import {
  getLiquidacionesConfiguracionesDeducciones,
  getLiquidacionConfiguracionDeduccion,
  postLiquidacionConfiguracionDeduccion,
  patchLiquidacionConfiguracionDeduccion,
  deleteLiquidacionConfiguracionDeduccion,
} from 'api/nominas/liquidaciones/liquidacionesConfiguracionesDeducciones';
import {
  ESTATUS_ACTIVO,
  ESTATUS_AUTORIZADO,
  ESTATUS_CANCELADO,
  ESTATUS_INACTIVO,
  ESTATUS_RECHAZADO,
} from 'utils/constants';

export const liquidacionConfiguracionDeduccionPermission = {
  permissionModel: 'liquidacionconfiguraciondeduccion',
  catalogsModels: [],
  tabsModels: [],
};

const LiquidacionConfiguracionDeduccion = ({ permission, liquidacionConfiguracion }) => {
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [estados, setEstados] = useState();
  const [data, setData] = useState();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [form] = Form.useForm();
  const [deducciones, setDeducciones] = useState();
  const [visibleAlert, setVisibleAlert] = useState();
  const [deduccionDelete, setDeduccionDelete] = useState();
  const [deduccionesFiltered, setDeduccionesFiltered] = useState();
  const [disableAuth, setDisableAuth] = useState(false);
  const [disableReactivar, setDisableReactivar] = useState(true);
  const [disableCancelar, setDisableCancelar] = useState(false);
  // Un registro Autorizado o Cancelado ya no debe poder editarse ni eliminarse --
  // cambiarlo ahi afecta de inmediato los calculos de ISR de las Liquidaciones de
  // Empleado que ya usan esta configuracion.
  const [disableEditDelete, setDisableEditDelete] = useState(true);
  const [confirmationVisible, setConfirmationVisible] = useState(false);
  const [visibleReactivarConfirmation, setVisibleReactivarConfirmation] = useState(false);
  const [visibleCancelarConfirmation, setVisibleCancelarConfirmation] = useState(false);

  const formInstances = [
    form,
  ];

  const formatReceivedClone = (values) => {
    if (Array.isArray(values)) {
      let valueClone = values.map((e) => formatReceivedValues(e));
      valueClone = valueClone.map((e) => ({
        ...e,
      }));
      return valueClone;
    }
    return formatReceivedValues(values);
  };

  const fetchData = async () => {
    setLoading(true);
    const _data = await getLiquidacionesConfiguracionesDeducciones();
    setData(formatReceivedClone(_data));
    setLoading(false);
  };

  const filterDeducciones = (_deducciones) => {
    const _deduccionesFiltered = _deducciones.filter((deduc) => !data.some((val) => val?.deduccion === deduc?.id));
    return _deduccionesFiltered;
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _estados,
        _deducciones,
        _data,
      ] = await Promise.all([
        getEstadosGlobales(),
        getDeducciones({ es_deduccion_de_liquidacion: true, estados_globales: ESTATUS_AUTORIZADO }),
        getLiquidacionesConfiguracionesDeducciones(),
      ]);
      if (mounted) {
        setEstados(_estados);
        setDeducciones(_deducciones);
        setData(_data);
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
  }, []);

  const onCancel = () => {
    setDeduccionesFiltered(filterDeducciones(deducciones));
    setSelectedRowKeys([]);
    setVisible(false);
    setDisableAuth(false);
    setDisableReactivar(true);
    setDisableCancelar(false);
    setDisableEditDelete(true);
    form.resetFields();
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deleteLiquidacionConfiguracionDeduccion(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = responseData || data.find((item) => item.id === key);
    if (!match) {
      setLoading(false);
      return;
    }
    // El doble clic en una fila llama a esta funcion directo, sin pasar por el
    // boton "Editar" del toolbar (ver onRow.onDoubleClick en components/Table.jsx)
    // -- por eso el bloqueo tiene que ir aqui tambien, no solo en el `disabled` del
    // boton. Cambiar la Deduccion de una configuracion ya Autorizada afectaria de
    // inmediato el calculo de ISR de Liquidaciones que ya la estan usando.
    if (match.estados_globales === ESTATUS_AUTORIZADO || match.estados_globales === ESTATUS_CANCELADO) {
      setLoading(false);
      message.warn('No se puede editar una Deducción Autorizada o Cancelada.');
      return;
    }
    const liquidacionConfigDeduccion = await getLiquidacionConfiguracionDeduccion(match.id);
    const deduccion = deducciones.find((e) => e?.id === liquidacionConfigDeduccion?.deduccion);
    const _deduccionesFiltered = filterDeducciones(deducciones);
    setTimeout(() => {
      if (liquidacionConfigDeduccion) {
        form.resetFields();
        setDeduccionesFiltered(_deduccionesFiltered.concat(deduccion));
        setTimeout(() => {
          form.setFieldsValue({
            ...formatReceivedClone(liquidacionConfigDeduccion),
            deduccion: deduccion?.id || null,
          });
          setVisible(true);
        });
      }
    });
    setLoading(false);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = {
        ...form.getFieldsValue(),
        liquidacion_configuracion: liquidacionConfiguracion?.id,
      };
      const [key] = selectedRowKeys;
      const saved = await (key
        ? patchLiquidacionConfiguracionDeduccion(key, values)
        : postLiquidacionConfiguracionDeduccion(values));
      if (saved) {
        if (_continue) {
          await fetchData();
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(
        err,
        setLoading,
        formInstances,
      );
    }
  };
  const onClickDelete = () => {
    setDeduccionDelete(deducciones.find((deduccion) => deduccion.id === parseInt(form.getFieldValue('deduccion'), 10))?.concepto);
    setVisibleAlert(true);
  };

  const handleOnFinishConfirmation = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess({}, 'No se puede autorizar una Deducción ya autorizada');
        return;
      }
      const vals = {
        ...form.getFieldsValue(),
        liquidacion_configuracion: liquidacionConfiguracion?.id,
        estados_globales: ESTATUS_AUTORIZADO,
      };
      const res = await patchLiquidacionConfiguracionDeduccion(key, vals);
      if (res) {
        onSuccess(res);
        await fetchData();
        setConfirmationVisible(false);
        onCancel();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnFinishReactivarConfirmation = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess({}, 'No se puede Reactivar una Deducción Activa');
        return;
      }
      const vals = {
        ...form.getFieldsValue(),
        liquidacion_configuracion: liquidacionConfiguracion?.id,
        estados_globales: ESTATUS_ACTIVO,
      };
      const res = await patchLiquidacionConfiguracionDeduccion(key, vals);
      if (res) {
        await fetchData();
        onSuccess(res);
        setVisibleReactivarConfirmation(false);
        onCancel();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnFinishCancelarConfirmation = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess({}, 'No se puede cancelar una Deducción ya cancelada');
        return;
      }
      const vals = {
        ...form.getFieldsValue(),
        liquidacion_configuracion: liquidacionConfiguracion?.id,
        estados_globales: ESTATUS_CANCELADO,
      };
      const res = await patchLiquidacionConfiguracionDeduccion(key, vals);
      if (res) {
        await fetchData();
        onSuccess(res);
        setVisibleCancelarConfirmation(false);
        onCancel();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    const isAutorizado = record?.estados_globales === ESTATUS_AUTORIZADO;
    const isInactivo = record?.estados_globales === ESTATUS_INACTIVO;
    const isActivo = record?.estados_globales === ESTATUS_ACTIVO;
    const isCancelado = record?.estados_globales === ESTATUS_CANCELADO;
    const isRechazado = record?.estados_globales === ESTATUS_RECHAZADO;
    setDisableAuth(isAutorizado || isInactivo || isCancelado || isRechazado);
    setDisableReactivar(isActivo);
    setDisableCancelar(isCancelado);
    setDisableEditDelete(isAutorizado || isCancelado);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
    contratacion: [requiredRule],
    estatus: [requiredRule],
  };

  const columns = [
    {
      titleText: 'Deducción',
      dataIndex: 'deduccion',
      key: 'deduccion',
      width: 300,
      render: (val) => {
        const configuracionesDeducciones = deducciones.find((deduccion) => deduccion.id === val);
        return configuracionesDeducciones ? `${configuracionesDeducciones?.clave} - ${configuracionesDeducciones?.concepto}` : '';
      },
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 300,
      render: (val) => estados.find((estado) => estado.id === val)?.descripcion,
    },
  ];

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          data={data}
          cols={columns}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit: () => onClickEdit(),
            onClickDelete,
          }}
          customActions={[
            {
              onClick: () => setConfirmationVisible(true),
              permission: { model: 'liquidacionconfiguraciondeduccion', action: 'autorizar' },
              text: 'Autorizar',
              icon: <CheckCircleOutlined />,
              disabledBySelection: false,
              className: 'success',
              disabled: !selectedRowKeys?.length || disableAuth,
            },
            {
              onClick: () => setVisibleReactivarConfirmation(true),
              permission: { model: 'liquidacionconfiguraciondeduccion', action: 'reactivar' },
              text: 'Reactivar',
              icon: <RedoOutlined />,
              disabledBySelection: false,
              className: 'success',
              disabled: !selectedRowKeys?.length || disableReactivar,
            },
            {
              onClick: () => setVisibleCancelarConfirmation(true),
              permission: { model: 'liquidacionconfiguraciondeduccion', action: 'cancelar' },
              text: 'Cancelar',
              icon: <CloseCircleOutlined />,
              disabledBySelection: false,
              className: 'cancel',
              disabled: !selectedRowKeys?.length || disableCancelar,
            },
          ]}
          permission={permission}
          allowExportPDF={false}
          disableEdit={!selectedRowKeys?.length || disableEditDelete}
          disableDelete={!selectedRowKeys?.length || disableEditDelete}
        />
        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          footer={null}
          width={500}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Deducción`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
            />
          )}
          onCancel={onCancel}
          forceRender
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            name="general"
          >
            <Col span={24}>
              <Form.Item
                name="deduccion"
                label="Deducción"
                rules={rules.required}
                hasFeedback
              >
                <Select
                  dataSource={deduccionesFiltered}
                  render={(val) => `${val?.clave} - ${val?.concepto}`}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="estados_globales"
                label="Estatus"
                rules={rules.estatus}
                hasFeedback
                initialValue={1}
              >
                <Select
                  labelProp="descripcion"
                  keyProp="id"
                  dataSource={estados}
                  disabled={selectedRowKeys.length <= 0}
                />
              </Form.Item>
            </Col>
          </Form>
        </Modal>
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={`Deducción ${deduccionDelete}`}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={handleOnFinishConfirmation}
          ConfirmAutorize
          onCancel={() => setConfirmationVisible(false)}
          open={confirmationVisible}
          contentTitle={`Autorizar la Deducción ${deducciones?.find((deduccion) => deduccion.id === form.getFieldValue('deduccion'))?.concepto || ''}`}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={handleOnFinishReactivarConfirmation}
          onFinishText="Reactivar"
          onFinishIcon={<RedoOutlined />}
          onCancel={() => {
            setVisibleReactivarConfirmation(false);
            onCancel();
          }}
          open={visibleReactivarConfirmation}
          contentTitle={`Reactivar la Deducción ${deducciones?.find((deduccion) => deduccion.id === form.getFieldValue('deduccion'))?.concepto || ''}`}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={handleOnFinishCancelarConfirmation}
          ConfirmCancel
          onCancel={() => {
            setVisibleCancelarConfirmation(false);
            onCancel();
          }}
          open={visibleCancelarConfirmation}
          contentTitle={`Cancelar la Deducción ${deducciones?.find((deduccion) => deduccion.id === form.getFieldValue('deduccion'))?.concepto || ''}`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default LiquidacionConfiguracionDeduccion;
